import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router";
import { Capacitor } from "@capacitor/core";

// Map route paths to tab IDs
const TAB_ROUTES: Record<string, string> = {
  "/": "home",
  "/blocks": "blocks",
  "/email": "email",
  "/codes": "codes",
  "/expenses": "expenses",
};

// Reverse mapping
const ROUTE_FROM_TAB: Record<string, string> = {
  home: "/",
  blocks: "/blocks",
  email: "/email",
  codes: "/codes",
  expenses: "/expenses",
};

// Tab configuration with SF Symbols
const TAB_ITEMS = [
  { id: "home", title: "Dashboard", systemIcon: "house" },
  { id: "blocks", title: "Blocks", systemIcon: "list.bullet" },
  { id: "email", title: "Messages", systemIcon: "envelope" },
  { id: "codes", title: "Codes", systemIcon: "key" },
  { id: "expenses", title: "Expenses", systemIcon: "receipt" },
];

interface NativeTabsContextValue {
  isNativeTabsActive: boolean;
  hideTabsBar: () => void;
  showTabsBar: () => void;
  setEmailBadge: (count: number) => void;
}

const NativeTabsContext = createContext<NativeTabsContextValue | null>(null);

export function NativeTabsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNativeTabsActive, setIsNativeTabsActive] = useState(false);
  const isInitializedRef = useRef(false);
  const listenerRef = useRef<{ remove: () => void } | null>(null);

  // Check if we're on iOS (native tabs only work on iOS 26+)
  const isNativePlatform = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

  // Store TabsBar reference for native platform
  const tabsBarRef = useRef<typeof import("stay-liquid").TabsBar | null>(null);

  // Initialize native tabs (only once on mount)
  useEffect(() => {
    if (!isNativePlatform || isInitializedRef.current) return;

    // Mark as initialized immediately to prevent re-runs
    isInitializedRef.current = true;

    const initializeTabs = async () => {
      try {
        // Dynamically import stay-liquid only on native iOS
        const { TabsBar } = await import("stay-liquid");
        tabsBarRef.current = TabsBar;

        // Get current tab from route
        const currentTabId = TAB_ROUTES[location.pathname] ?? "home";

        await TabsBar.configure({
          items: TAB_ITEMS,
          initialId: currentTabId,
          visible: true,
          selectedIconColor: "#007AFF", // iOS blue
          unselectedIconColor: "#8E8E93", // iOS gray
        });

        // Listen for tab selection
        listenerRef.current = await TabsBar.addListener("selected", ({ id }) => {
          const route = ROUTE_FROM_TAB[id];
          if (route) {
            navigate(route);
          }
        });

        setIsNativeTabsActive(true);
      } catch (error) {
        // Native tabs not available (iOS < 26 or other issue)
        console.log("Native tabs not available:", error);
        setIsNativeTabsActive(false);
      }
    };

    initializeTabs();

    return () => {
      listenerRef.current?.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativePlatform]);

  // Sync tab selection with route changes
  useEffect(() => {
    if (!isNativeTabsActive || !isInitializedRef.current || !tabsBarRef.current) return;

    const tabId = TAB_ROUTES[location.pathname];
    if (tabId) {
      tabsBarRef.current.select({ id: tabId }).catch(() => {});
    }
  }, [location.pathname, isNativeTabsActive]);

  const hideTabsBar = useCallback(() => {
    if (isNativeTabsActive && tabsBarRef.current) {
      tabsBarRef.current.hide().catch(() => {});
    }
  }, [isNativeTabsActive]);

  const showTabsBar = useCallback(() => {
    if (isNativeTabsActive && tabsBarRef.current) {
      tabsBarRef.current.show().catch(() => {});
    }
  }, [isNativeTabsActive]);

  const setEmailBadge = useCallback((count: number) => {
    if (isNativeTabsActive && tabsBarRef.current) {
      tabsBarRef.current.setBadge({
        id: "email",
        value: count > 0 ? count : null,
      }).catch(() => {});
    }
  }, [isNativeTabsActive]);

  return (
    <NativeTabsContext.Provider
      value={{ isNativeTabsActive, hideTabsBar, showTabsBar, setEmailBadge }}
    >
      {children}
    </NativeTabsContext.Provider>
  );
}

export function useNativeTabs(): NativeTabsContextValue {
  const context = useContext(NativeTabsContext);
  if (!context) {
    // Return no-op defaults if used outside provider
    return {
      isNativeTabsActive: false,
      hideTabsBar: () => {},
      showTabsBar: () => {},
      setEmailBadge: () => {},
    };
  }
  return context;
}
